import i18n from "../i18n";
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Play, Square, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const CASH_METHODS = ["cash", "gcash", "maya", "bank", "cheque"];

export function ShiftBanner({ onChanged }) {
    const [shift, setShift] = useState(null);
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [dlgOpen, setDlgOpen] = useState(false);
    const [dlgClose, setDlgClose] = useState(false);
    const [openingFloat, setOpeningFloat] = useState("");
    const [openingNotes, setOpeningNotes] = useState("");
    const [counted, setCounted] = useState({});
    const [closeNotes, setCloseNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [summary, setSummary] = useState(null);

    const cur = (v) => formatCurrency(v, farm.currency);

    const load = useCallback(async () => {
        try {
            const [s, f] = await Promise.all([
                api.get("/pos/shifts/current").then(r => r.data),
                api.get("/farm").then(r => r.data),
            ]);
            setShift(s && s.id ? s : null);
            setFarm(f);
            if (s && s.id) {
                const full = await api.get(`/pos/shifts/${s.id}`).then(r => r.data);
                setSummary(full.summary);
            }
        } catch (e) {
            console.error("Shift load failed:", e);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openShift = async () => {
        setBusy(true);
        try {
            await api.post("/pos/shifts/open", { opening_float: Number(openingFloat) || 0, notes: openingNotes });
            toast.success("Shift opened. All sales will be tracked.");
            setDlgOpen(false); setOpeningFloat(""); setOpeningNotes("");
            await load(); onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to open shift");
        } finally { setBusy(false); }
    };

    const closeShift = async () => {
        if (!shift) return;
        setBusy(true);
        try {
            const r = await api.post(`/pos/shifts/${shift.id}/close`, {
                counted, notes: closeNotes,
            });
            const variance = Number(r.data.variance_cash || 0);
            if (variance === 0) toast.success("Shift closed. Cash matches expected! ✅");
            else if (variance < 0) toast.error(`Shift closed. Cash SHORT by ${cur(Math.abs(variance))}`);
            else toast.warning(`Shift closed. Cash OVER by ${cur(variance)}`);
            setDlgClose(false); setCounted({}); setCloseNotes("");
            await load(); onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Close failed");
        } finally { setBusy(false); }
    };

    const expectedCash = summary ? Number(summary.by_method?.cash?.total || 0) + Number(shift?.opening_float || 0) : 0;

    if (!shift) {
        return (
            <>
                <Card className="p-4 border-amber-500/40 bg-amber-500/5 flex items-center gap-3" data-testid="shift-banner-closed">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="flex-1">
                        <div className="font-semibold text-sm">{i18n.t("batch1.9416d69a3e82")}</div>
                        <div className="text-xs text-muted-foreground">{i18n.t("batch1.603a1d24ed40")}</div>
                    </div>
                    <Button size="sm" onClick={() => setDlgOpen(true)} className="h-9" data-testid="open-shift-btn"><Play className="w-4 h-4 mr-1" />{i18n.t("batch1.09509cdf6759")}</Button>
                </Card>
                <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
                    <DialogContent data-testid="open-shift-dialog">
                        <DialogHeader><DialogTitle>{i18n.t("batch1.2dd1098a9b10")}</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label className="tiny-label">{i18n.t("batch1.0789dc9c5a6a")}</Label>
                                <Input type="number" value={openingFloat} onChange={e => setOpeningFloat(e.target.value)} placeholder={i18n.t("batch1.bc628f9fdf5d")} className="h-12 mt-1 font-mono text-lg" autoFocus data-testid="opening-float-input" />
                            </div>
                            <div>
                                <Label className="tiny-label">{i18n.t("batch1.4d56ca9be0c8")}</Label>
                                <Textarea value={openingNotes} onChange={e => setOpeningNotes(e.target.value)} rows={2} placeholder={i18n.t("batch1.2db74df2c056")} />
                            </div>
                            <Button onClick={openShift} disabled={busy} className="w-full h-12 font-bold" data-testid="confirm-open-shift-btn">
                                <Play className="w-4 h-4 mr-2" />{i18n.t("batch1.00eb6118aa8c")}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </>
        );
    }

    // Shift open: show status + close button
    const openedAt = new Date(shift.opened_at);
    const runtimeMs = Date.now() - openedAt.getTime();
    const runtimeH = Math.floor(runtimeMs / 3_600_000);
    const runtimeM = Math.floor((runtimeMs % 3_600_000) / 60_000);

    return (
        <>
            <Card className="p-4 border-primary/40 bg-primary/5" data-testid="shift-banner-open">
                <div className="flex items-center gap-3 flex-wrap">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm">{i18n.t("batch1.d8eb2cd577f1")} {shift.cashier_name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap">
                            <span><Clock className="w-3 h-3 inline mr-1" />{runtimeH}{i18n.t("batch1.27d5482eebd0")} {runtimeM}{i18n.t("batch1.512a3bb93d16")}</span>
                            <span>{i18n.t("batch1.5a308463b948")} <span className="font-mono font-semibold text-foreground">{cur(shift.opening_float)}</span></span>
                            {summary && <span>{i18n.t("batch1.34a4dd3cb9f3")} <span className="font-mono font-semibold text-foreground">{cur(summary.gross_sales)}</span> ({summary.invoice_count} {i18n.t("batch1.08aeea7adb7f")}</span>}
                        </div>
                    </div>
                    <Button size="sm" variant="destructive" onClick={() => setDlgClose(true)} className="h-9" data-testid="close-shift-btn"><Square className="w-4 h-4 mr-1" />{i18n.t("batch1.7fae2e54bfad")}</Button>
                </div>
            </Card>

            <Dialog open={dlgClose} onOpenChange={setDlgClose}>
                <DialogContent className="max-w-lg" data-testid="close-shift-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("batch1.b4753fad7c09")}</DialogTitle>
                        <DialogDescription>{i18n.t("batch1.aa49ea3a3849")}</DialogDescription>
                    </DialogHeader>
                    {summary && <div className="space-y-4">
                        <div className="rounded-xl bg-secondary p-3 text-sm space-y-1">
                            <div className="flex justify-between"><span>{i18n.t("batch1.8c3ceb9d7a63")}</span><span className="font-mono">{cur(shift.opening_float)}</span></div>
                            <div className="flex justify-between"><span>{i18n.t("batch1.68b06689017c")}</span><span className="font-mono">{cur(summary.by_method?.cash?.total || 0)}</span></div>
                            <div className="flex justify-between font-bold pt-2 border-t border-border"><span>{i18n.t("batch1.a7520b9f06a5")}</span><span className="font-mono text-primary">{cur(expectedCash)}</span></div>
                        </div>
                        <p className="text-sm">{i18n.t("batch1.00ef3a724f65")}</p>
                        {CASH_METHODS.map(m => {
                            const expected = m === "cash" ? expectedCash : (summary.by_method?.[m]?.total || 0);
                            if (m !== "cash" && Number(expected) === 0) return null;  // hide zero rows for non-cash
                            const c = counted[m] ?? "";
                            const varN = c !== "" ? Number(c) - expected : null;
                            return (
                                <div key={m} className="grid grid-cols-3 gap-2 items-center">
                                    <Label className="capitalize font-semibold">{m}</Label>
                                    <div className="text-xs text-muted-foreground text-right">{i18n.t("batch1.6ac87f344682")}<br /><span className="font-mono text-foreground">{cur(expected)}</span></div>
                                    <div>
                                        <Input type="number" value={c} placeholder={i18n.t("batch1.319a2b6e851d")} onChange={e => setCounted({ ...counted, [m]: e.target.value })} className="h-11 font-mono text-right" data-testid={`counted-${m}-input`} />
                                        {varN !== null && Math.abs(varN) > 0.01 && (
                                            <div className={`text-xs font-mono text-right mt-0.5 ${varN < 0 ? "text-destructive" : "text-amber-600"}`}>
                                                {varN < 0 ? "SHORT " : "OVER "}{cur(Math.abs(varN))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <div>
                            <Label className="tiny-label">{i18n.t("batch1.4d56ca9be0c8")}</Label>
                            <Textarea value={closeNotes} onChange={e => setCloseNotes(e.target.value)} rows={2} placeholder={i18n.t("batch1.59a4e9d0dcd7")} />
                        </div>
                        <Button onClick={closeShift} disabled={busy} className="w-full h-12 font-bold" variant="destructive" data-testid="confirm-close-shift-btn">
                            <Square className="w-4 h-4 mr-2" />{i18n.t("batch1.092f9dd0a820")}
                        </Button>
                    </div>}
                </DialogContent>
            </Dialog>
        </>
    );
}
